import { useCallback, useState } from 'react'
import { ArrowUp, Paperclip } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@neo-agents/ui'
import { useAutoResizeTextarea } from '../hooks/useAutoResizeTextarea'
import { ATTACHMENT_ACCEPT, useAttachmentUpload } from '../hooks/useAttachmentUpload'
import { chatInputActionsRef } from './ChatInput'
import type { ChatAttachment } from '../state/types'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { AttachmentList } from './AttachmentList'

export function GeneralChatHome() {
  const { memberName } = useWorkspace()
  const [value, setValue] = useState('')
  const { textareaRef, resize } = useAutoResizeTextarea()
  const { attachments, addFiles, removeAttachment, clearAttachments, isUploadingAttachments } = useAttachmentUpload()
  const hasIncompleteAttachments = attachments.some((attachment) => attachment.uploadStatus !== 'uploaded')
  const canSend = (value.trim().length > 0 || attachments.length > 0) && !hasIncompleteAttachments

  const handleSend = useCallback(() => {
    const content = value.trim()
    if (!content && attachments.length === 0) return
    chatInputActionsRef.current.onSend?.(content, attachments)
    setValue('')
    clearAttachments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [attachments, clearAttachments, textareaRef, value])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = ATTACHMENT_ACCEPT
    input.onchange = async () => {
      if (!input.files) return
      await addFiles(Array.from(input.files))
    }
    input.click()
  }, [addFiles])

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-[560px]">
        <h1 className="text-center font-heading text-4xl text-foreground">
          Hey {memberName || 'there'}!
        </h1>
        <div className="mt-8 rounded-base border-2 border-nb-border bg-secondary-bg nb-shadow">
          <AttachmentList attachments={attachments} onRemove={removeAttachment} />

          <div className="px-4 pt-3 pb-2">
            <textarea
              ref={textareaRef}
              className="min-h-[52px] max-h-[200px] w-full resize-none bg-transparent text-base text-foreground outline-none placeholder:text-foreground/40"
              placeholder="Ask me anything..."
              rows={2}
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                resize()
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex items-center justify-between border-t-2 border-nb-border px-3 py-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="rounded-base border-2 border-nb-border bg-bg p-1.5 text-foreground transition-colors hover:bg-main hover:text-main-foreground"
                  onClick={handleFileUpload}
                  type="button"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Upload attachment</TooltipContent>
            </Tooltip>
            <button
              className={cn(
                'rounded-base border-2 border-nb-border p-1.5 font-heading transition-all',
                canSend
                  ? 'bg-main text-main-foreground nb-shadow-sm nb-hover-press'
                  : 'cursor-not-allowed bg-foreground/10 text-foreground/30',
              )}
              disabled={!canSend}
              onClick={handleSend}
              type="button"
              title={
                isUploadingAttachments
                  ? 'Wait for attachments to finish uploading'
                  : hasIncompleteAttachments
                    ? 'Remove failed attachments before sending'
                    : 'Send message'
              }
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
